import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useEffect, useMemo } from "react";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import api from "services/api";
import COMMON from "common";
import { formatDatePattern } from "common/calendar";
import { promptAlertMessage } from "store/slices/alert";
import serveRequestErrors, { axiosCancelled } from "common/serve-request-errors";
import AppStatus from "components/app-status";
import pdfIcon from "assets/images/pdf-icon.svg";
import rejectIcon from "assets/images/components/pages/leave-management/reject-icon.svg";
import checkedIcon from "assets/images/components/pages/leave-management/checked-icon.svg";
import AppCloseIcon from "components/icons/app-close-icon";
import AppButton from "components/app-button";

export const AppLeaveModal = (props, ref) => {
	const [data, setData] = useState({});
	const [visible, setVisible] = useState(false);
	const dispatch = useDispatch();
	const {onHandleGetList, rejectReasonRef, showActions} = props;

	const onHandleDismiss = useCallback(() => {
		setData({});
		setVisible(false);
	}, []);

	const onHandleShow = useCallback((id) => {
		setData((prev) => ({ ...prev, id }));
		setVisible(true);
	}, []);

	const onHandleApproveLeave = useCallback(async () => {
		try {
			await api.post.leaves.approve({ ids : [data.id] });
			dispatch(promptAlertMessage({ message: "Leave has been approved successfully" }));
			onHandleGetList?.();
			onHandleDismiss();
		} catch (error) {
			serveRequestErrors(error);
		}
	}, [data.id, dispatch, onHandleDismiss, onHandleGetList]);

	const onHandleRejectLeave = useCallback(() => {
		rejectReasonRef?.current.onHandleShow([data.id]);  
		onHandleDismiss();
	}, [data.id, onHandleDismiss, rejectReasonRef]);

	const productInfo = useMemo(() => {
		const companyLeaves = data?.companyLeaveApplicationDetails;
		const rejectReason = data?.status === COMMON.STATUS_ID.REJECTED ? { label: "Reject Reason", value: data?.rejectReason || "-" } : {};

		const info = [
			{ label: "User Name", value: data.employeeDetail?.personalInfo?.name || "-" },
			{ label: "Employee ID", value: data.employeeDetail?.employeeId || "-" },
			{ label: "Time Off", value: data.timeOff?.label || "-" },
			{ label: "Days", value: data.noOfDays || "-" },
			{ label: "Start Date", value: companyLeaves?.length ? formatDatePattern(companyLeaves.slice(0).shift().date) : "-" },
			{ label: "End Date", value: companyLeaves?.length ? formatDatePattern(companyLeaves.slice(0).pop().date) : "-" },			
			{ label: "Submit Date", value: data.createdDate ? formatDatePattern(data.createdDate) : "-" },
			{ label: "Attachment", value: data?.companyLeaveApplicationFiles?.[0] || "-" },
			{ label: "Description", value: data.description || "-" },
			...(rejectReason.label ? [rejectReason] : []),
			({ label: "Remarks", value: data?.remarks || "-" }),
		];
		
		return info;
	}, [data]);

	const checkerButtons = useMemo(() => {
		if (data.status !== COMMON.STATUS_ID.PENDING) return [];
		return [
			{ label: "Reject Leave", outline: true, icon: rejectIcon, onClick: onHandleRejectLeave },
			{ label: "Approve Leave", icon: checkedIcon, onClick: onHandleApproveLeave },
		];
	}, [data.status, onHandleRejectLeave, onHandleApproveLeave]);
	
	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	useEffect(() => {
		const onHandleGetDetails = async () => {
			let response = null;
			try {
				response = await api.get.leaves.leave(data.id);
			} catch (error) {
				// the record may be gone when arriving from a notification, so close
				// rather than leave an empty modal behind the error toast
				if (!axiosCancelled(serveRequestErrors(error))) onHandleDismiss();
			}
			if (response) {
				setData((prev) => ({ ...prev, ...response }));
			}
		};

		if (data.id) onHandleGetDetails();
	}, [data.id, onHandleDismiss]);

	return (
		<Modal classes={{ root: "app-leave-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="leave-modal" aria-describedby="leave-modal">
			<div className="leave-modal">
				<div className="leave-modal__main">
					<button className="leave-modal__close-button" onClick={onHandleDismiss}>
						<AppCloseIcon color="#666666" />
					</button>

					<div className="leave-modal__header">
						<div className="leave-modal__profile">
							<p className="leave-modal__title">Leave Details</p>
						</div>
					</div>

					<div className="info">
						<div className="info__box">
							<p className="leave-modal__text">Leave Type</p>
							<p className="leave-modal__title">{data.leaveType}</p>
						</div>
						<div className="info__box">
							<p className="leave-modal__text">Status</p>
							<div className="leave-modal__title">
								<AppStatus status={data?.status || "-"} />
							</div>
						</div>
					</div>

					<div className="leave-modal__body">
						<div className="leave-modal__container">
							<div className="leave-modal__wrapper">
								{productInfo.map((o, i) => {
									const fileName = o.value?.fileName;

									return (
										<div className="leave-modal__info" key={i}>
											<p className="leave-modal__label">{o.label}</p>
											{!fileName && <p className="leave-modal__value">{o.value}</p>}
											{fileName && (
												<p className="leave-modal__value">
													<a href={o.value.fileURL} className="leave-modal__link" target="_blank" rel="noopener noreferrer">
														<img src={pdfIcon} alt="attachment" /> {fileName}
													</a>
												</p>
											)}
										</div>
									);
								})}
							</div>
							<div className="leave-modal__button-container">
								{showActions && checkerButtons.map((btn, i) => (
									<AppButton key={i} type="button" outline={btn.outline} icon={btn.icon} label={btn.label} onClick={btn.onClick} />
								))}
							</div>
						</div>						
					</div>									
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppLeaveModal));

AppLeaveModal.propTypes = {
	ref: PropTypes.object,
	onHandleGetList: PropTypes.func,
	rejectReasonRef: PropTypes.object,
	showActions: PropTypes.bool,
};
